import { ChartPieIcon } from "@phosphor-icons/react"
import { useNavigate } from "@tanstack/react-router"

import { Button } from "~/components/design-system/components"
import { routeBaseFor } from "~/components/demand-2/demandRoutes"
import { useBudgetMarkdowns } from "~/components/demand-2/shared/useBudgetMarkdowns"
import WarningNotice from "~/components/demand-2/shared/WarningNotice"
import { useDemand2Scope } from "~/components/demand-2/useDemand2Scope"
import { formatName } from "~/utils/namingUtils"

/**
 * Under the selected whole-collection discounts answer: Financial reconciliation can show the
 * budget's markdown only against a plan that keeps discounting, so say what this answer does there.
 * Silent until the budget plans markdown, since before that nothing there changes.
 */
const MarkdownVisibilityNote = ({ optionId }: { optionId: string }) => {
  const navigate = useNavigate()
  const { scopeId, isGroup, kind } = useDemand2Scope()
  const { budgetName, hasMarkdowns } = useBudgetMarkdowns()
  if (!hasMarkdowns || budgetName == null || kind == null) return null

  // A fresh search drops `strategy`, so the dialog closes on the way.
  const openReconciliation = () =>
    void navigate({
      to: `${routeBaseFor(kind)}/budget`,
      params: { collectionId: String(scopeId) },
      search: isGroup ? { isGroup: true } : undefined,
    })

  if (optionId === "remove") {
    return (
      <WarningNotice
        className="ml-8"
        title="This turns off markdown visibility in Financial reconciliation"
        actions={
          <Button variant="neutral" size="sm" onClick={openReconciliation}>
            See what changes
          </Button>
        }
      >
        With discounts removed, the demand plan assumes full-price {formatName("sale", true)}.
        Markdown weeks and expected margin from the {budgetName} budget are hidden until discount
        impact is enabled again.
      </WarningNotice>
    )
  }
  if (optionId !== "maintain") return null
  return (
    <div className="ml-8 flex flex-wrap items-center gap-x-2 gap-y-1 paragraph-sm text-fg-muted">
      <span>Markdown weeks and expected margin are shown in Financial reconciliation.</span>
      <Button variant="transparent" size="xs" onClick={openReconciliation}>
        <ChartPieIcon /> Open Financial reconciliation
      </Button>
    </div>
  )
}

export default MarkdownVisibilityNote
